/**
 * Editor for one `SloSet` (a list of SLO items + their aggregator).
 *
 * Each SLO row exposes the kind selector, comparator, target value
 * and the continuous-violation window. `kind=custom` reveals a
 * PromQL textarea; the Envoy preset kinds keep the PromQL hidden —
 * the autoscaler hits the Envoy admin counter directly at evaluation
 * time (see `MetricsBackend.evaluate_slo` in
 * `controller/controller/autoscaler/metrics.py`).
 *
 * The backend a SLO reads from is fixed per kind (no service-wide
 * source toggle). The kind picker is grouped into:
 *
 *   - **Envoy admin** — derived from the data plane backstage already
 *     runs (`/stats` on `envoy:9901`). Always available, no pod-side
 *     exporter required: RPS/pod, 5xx error rate, p99 upstream
 *     latency, pending-requests gauge. Envoy stat names are fixed so
 *     a preset picker is meaningful here.
 *   - **Advanced** — operator-supplied custom PromQL. This is the
 *     sole Prometheus path: backstage ships no Prom-side presets
 *     because pod-side metric names are NOT standardised across
 *     runtimes (vLLM ≠ TGI ≠ DCGM ≠ BYO), so any baked-in name would
 *     silently return "no data" on most workloads. Operators paste
 *     the actual PromQL their workload exposes.
 *
 * Operators can mix Envoy presets and `custom` PromQL freely in the
 * same SloSet — the autoscaler picks the right backend per row.
 *
 * The editor is intentionally controlled and pure: the parent owns
 * the value and `onChange` is fired on every edit. Persistence is the
 * caller's job (typically a PATCH to /services/{id} or /pools/{id}
 * after a Save click).
 */
import { useRef } from "react";
import { Plus, Trash2 } from "lucide-react";

import type {
  SLO,
  SloAggregator,
  SloComparator,
  SloKind,
  SloSet,
} from "@/api/types";
import { useMetricDiscovery } from "@/components/MetricCatalogDrawer";
import { Pill } from "@/components/Pill";

interface SLOEditorProps {
  value: SloSet;
  onChange: (next: SloSet) => void;
  /** Disable all controls (e.g. while inheriting from parent scope). */
  disabled?: boolean;
  /**
   * Service id used to scrape live metric metadata for the
   * `kind=custom` PromQL textarea's autocomplete + Discover drawer.
   * Both the service-level SLO editor (Service.id) and the pool-level
   * one (Pool.service_id) pass this — when omitted, the custom
   * textarea still works but gets no autocomplete / discover button
   * (the old behaviour).
   */
  serviceId?: number;
}

const KIND_LABELS: Record<SloKind, string> = {
  rps_per_pod: "RPS / pod",
  error_rate_envoy: "5xx error rate",
  p99_latency_envoy: "p99 upstream latency",
  pending_requests_envoy: "pending requests (saturation)",
  inflight_saturation_envoy: "in-flight saturation",
  custom: "custom PromQL",
};

/**
 * Unit shown next to `target_value` so it's obvious whether 10 means
 * "10 req/s", "10 ms", or "10%". Empty string for `custom` (the unit
 * depends on what the operator's PromQL returns).
 */
const KIND_UNITS: Record<SloKind, string> = {
  rps_per_pod: "req/s",
  error_rate_envoy: "%",
  p99_latency_envoy: "ms",
  pending_requests_envoy: "reqs",
  inflight_saturation_envoy: "req/pod",
  custom: "",
};

const KIND_HINTS: Record<SloKind, string> = {
  rps_per_pod:
    "req/s per ready pod, from `cluster.<svc>.upstream_rq_total / membership_total` on Envoy admin. Typical target: 10–20 depending on workload throughput.",
  error_rate_envoy:
    "5xx percentage over the delta window, from `Δ(upstream_rq_5xx) / Δ(upstream_rq_total)`. Returns `no signal` while the cluster is idle (no requests in the interval). Typical target: 1 (= 1%).",
  p99_latency_envoy:
    "ms — interval P99 of the `upstream_rq_time` histogram on Envoy admin. Reflects what the data plane sees, NOT the workload's internal queue wait. Typical target: 500–2000 ms.",
  pending_requests_envoy:
    "reqs — current value of `upstream_rq_pending_active`. Saturation signal (Envoy is buffering because every endpoint is busy). Rises before tail latency does, so it's a good early scale-up trigger.",
  inflight_saturation_envoy:
    "req/pod — concurrent in-flight requests per ready endpoint, from `upstream_rq_active / membership_total`. The right scale-up signal for high-latency workloads (TTS, image gen, inference) where `rps_per_pod` is structurally small because each request pins the GPU for multiple seconds. Typical target: 0.8 (= scale up when average pod is 80% utilised).",
  custom:
    "Provide a PromQL scalar that the controller will compare against `target_value`. Always evaluated against Prometheus / GMP. Typical: `vllm:request_queue_time_seconds` (vLLM), `tgi_request_queue_duration` (TGI), `DCGM_FI_DEV_GPU_UTIL` (DCGM) — pick whatever your workload actually exports.",
};

/**
 * Default `target_value` per kind, used when the operator picks a new
 * kind whose existing target value is meaningless under the new unit
 * (e.g. switching from "10 req/s" to a latency SLO — 10 ms is far too
 * tight). We only swap when the current value matches the *previous*
 * kind's default; an operator who already typed a custom number gets
 * to keep it.
 */
const KIND_TARGET_DEFAULTS: Record<SloKind, number> = {
  rps_per_pod: 10,
  error_rate_envoy: 1, // %
  p99_latency_envoy: 1000, // ms
  pending_requests_envoy: 50,
  inflight_saturation_envoy: 0.8, // 80% utilisation
  custom: 0,
};

/**
 * `<optgroup>` ordering for the kind picker. Envoy first because it
 * works on every cluster out-of-the-box; ``custom`` last because the
 * operator is committing to writing PromQL by hand. There is no
 * dedicated "Prometheus presets" group — pod-side metric names are
 * not standardised, so any preset baked into the picker would
 * silently return "no data" on most workloads. ``custom`` is the
 * single Prom path; `KIND_HINTS.custom` lists the runtime-specific
 * metric names operators typically reach for.
 */
const KIND_GROUPS: Array<{ label: string; kinds: SloKind[] }> = [
  {
    label: "Envoy admin · always available",
    kinds: [
      "rps_per_pod",
      "error_rate_envoy",
      "p99_latency_envoy",
      "pending_requests_envoy",
      "inflight_saturation_envoy",
    ],
  },
  {
    label: "Advanced · custom PromQL",
    kinds: ["custom"],
  },
];

const ENVOY_KINDS: ReadonlySet<SloKind> = new Set(KIND_GROUPS[0].kinds);

/**
 * True iff the SLO `kind` is answered by the Envoy admin client. Used
 * for the per-row source badge — keeping it here (rather than mirroring
 * `_ENVOY_KINDS` from the backend in a separate constant) means we
 * can't accidentally drift between the picker grouping and the badge.
 */
function isEnvoyKind(kind: SloKind): boolean {
  return ENVOY_KINDS.has(kind);
}

function freshSLO(): SLO {
  // Default a freshly-added SLO row to ``rps_per_pod`` because it is
  // the safest preset the Envoy admin /stats endpoint can answer
  // out-of-the-box (see backend ``MetricsBackend`` capability matrix).
  // Operators tune it from here and add Prom-backed SLOs side-by-side
  // once their pod-side exporters are wired up.
  return {
    kind: "rps_per_pod",
    comparator: ">",
    target_value: KIND_TARGET_DEFAULTS.rps_per_pod,
    for_seconds: 60,
    promql_expr: null,
  };
}

export function SLOEditor({
  value,
  onChange,
  disabled,
  serviceId,
}: SLOEditorProps) {
  // `useMetricDiscovery` is always called (rules-of-hooks) but only
  // wires the discover button + datalist when a serviceId is present.
  // The hook is cheap when `enabled` stays false (no network).
  const discovery = useMetricDiscovery({
    serviceId: serviceId ?? 0,
    disabled,
  });
  const discoveryEnabled = serviceId != null && serviceId > 0;
  const hasCustomRow = value.items.some((s) => s.kind === "custom");

  const setItem = (idx: number, patch: Partial<SLO>) => {
    const next = value.items.slice();
    const prev = next[idx];
    const merged = { ...prev, ...patch };
    if (merged.kind !== "custom") merged.promql_expr = null;
    // When the operator switches kind and their `target_value` is still
    // the *previous* kind's default (i.e. they never customised it),
    // swap to the new kind's default so the units make sense — "10
    // req/s" must not silently become "10 ms" when picking a latency
    // SLO. If the value differs from the old default, we trust that
    // it was operator-typed and leave it alone.
    if (
      patch.kind !== undefined &&
      patch.kind !== prev.kind &&
      prev.target_value === KIND_TARGET_DEFAULTS[prev.kind] &&
      merged.target_value === prev.target_value
    ) {
      merged.target_value = KIND_TARGET_DEFAULTS[patch.kind];
    }
    next[idx] = merged;
    onChange({ ...value, items: next });
  };

  const removeItem = (idx: number) => {
    onChange({ ...value, items: value.items.filter((_, i) => i !== idx) });
  };

  const addItem = () => {
    onChange({ ...value, items: [...value.items, freshSLO()] });
  };

  return (
    <div className={`slo-editor${disabled ? " is-disabled" : ""}`}>
      <div
        className="slo-editor__header"
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span className="muted" style={{ fontSize: 12 }}>
          aggregator
        </span>
        <select
          value={value.aggregator}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              aggregator: e.target.value as SloAggregator,
            })
          }
        >
          <option value="any">
            any violated → scale up (default)
          </option>
          <option value="all">all violated → scale up</option>
        </select>
        <span style={{ flex: 1 }} />
        {discoveryEnabled && hasCustomRow && discovery.discoverButton}
        <Pill tone={value.items.length === 0 ? "neutral" : "info"}>
          {value.items.length} SLO{value.items.length === 1 ? "" : "s"}
        </Pill>
      </div>
      {discoveryEnabled && discovery.catalogElements}

      <div className="slo-editor__rows" style={{ display: "grid", gap: 10 }}>
        {value.items.map((slo, idx) => (
          <div
            key={idx}
            className="subblock"
            style={{ padding: 12, display: "grid", gap: 8 }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <select
                value={slo.kind}
                disabled={disabled}
                onChange={(e) =>
                  setItem(idx, { kind: e.target.value as SloKind })
                }
                title="Metric driving this SLO. Source (Envoy / Prometheus) is fixed per kind — see the optgroup label."
                style={{ minWidth: 220 }}
              >
                {KIND_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.kinds.map((k) => (
                      <option key={k} value={k}>
                        {KIND_LABELS[k]}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <select
                value={slo.comparator}
                disabled={disabled}
                onChange={(e) =>
                  setItem(idx, {
                    comparator: e.target.value as SloComparator,
                  })
                }
                title="Comparator: when does this SLO count as violated?"
              >
                <option value=">">&gt;</option>
                <option value=">=">≥</option>
                <option value="<">&lt;</option>
                <option value="<=">≤</option>
              </select>
              <input
                type="number"
                value={slo.target_value}
                onChange={(e) =>
                  setItem(idx, { target_value: Number(e.target.value) })
                }
                disabled={disabled}
                style={{ width: 110 }}
                step="any"
              />
              {KIND_UNITS[slo.kind] && (
                <span
                  className="muted mono"
                  style={{ fontSize: 12, minWidth: 36 }}
                >
                  {KIND_UNITS[slo.kind]}
                </span>
              )}
              <span className="muted" style={{ fontSize: 12 }}>
                for
              </span>
              <input
                type="number"
                min={0}
                value={slo.for_seconds}
                onChange={(e) =>
                  setItem(idx, { for_seconds: Number(e.target.value) })
                }
                disabled={disabled}
                style={{ width: 80 }}
              />
              <span className="muted" style={{ fontSize: 12 }}>
                s
              </span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => removeItem(idx)}
                disabled={disabled}
                aria-label="remove SLO"
                title="Remove this SLO"
              >
                <Trash2 />
              </button>
            </div>
            {slo.kind === "custom" && (
              <CustomPromqlField
                value={slo.promql_expr ?? ""}
                onChange={(next) => setItem(idx, { promql_expr: next })}
                disabled={disabled}
                datalistId={
                  discoveryEnabled ? discovery.datalistId : undefined
                }
              />
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
              }}
            >
              <Pill tone={isEnvoyKind(slo.kind) ? "ok" : "info"}>
                {isEnvoyKind(slo.kind) ? "Envoy admin" : "Prometheus"}
              </Pill>
              <span className="muted">{KIND_HINTS[slo.kind]}</span>
            </div>
          </div>
        ))}
        {value.items.length === 0 && (
          <div
            className="empty"
            style={{
              padding: 16,
              border: "1px dashed var(--edge-strong)",
              borderRadius: 8,
              textAlign: "center",
              fontSize: 12,
            }}
          >
            no SLOs configured — the autoscaler falls back to the legacy
            QPS heuristic for this scope.
          </div>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={addItem}
          disabled={disabled}
        >
          <Plus />
          add SLO
        </button>
      </div>
    </div>
  );
}

interface CustomPromqlFieldProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /**
   * `<datalist>` id surfaced by the metric-discovery hook. When set,
   * an "insert metric" picker is rendered above the textarea — picking
   * a name (autocompleted from the workload's live `/metrics`) inserts
   * it at the textarea cursor. `<textarea>` doesn't natively support
   * `list=`, so the picker is a small companion `<input>` that pipes
   * its value back through this ref-driven helper.
   */
  datalistId?: string;
}

function CustomPromqlField({
  value,
  onChange,
  disabled,
  datalistId,
}: CustomPromqlFieldProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pickerRef = useRef<HTMLInputElement | null>(null);

  const insertMetric = (name: string) => {
    const cleaned = name.trim();
    if (!cleaned) return;
    const ta = textareaRef.current;
    if (!ta) {
      onChange(value ? `${value}${cleaned}` : cleaned);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const next = value.slice(0, start) + cleaned + value.slice(end);
    onChange(next);
    // Restore focus + place caret right after the inserted name on
    // the next tick (after React flushes the controlled value).
    queueMicrotask(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      const caret = start + cleaned.length;
      node.setSelectionRange(caret, caret);
    });
  };

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {datalistId && (
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            fontSize: 11,
          }}
        >
          <span className="muted" style={{ minWidth: 92 }}>
            insert metric
          </span>
          <input
            ref={pickerRef}
            list={datalistId}
            placeholder="pick or type a metric name from /metrics"
            className="mono"
            style={{ flex: 1, fontSize: 12 }}
            disabled={disabled}
            spellCheck={false}
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              insertMetric(e.currentTarget.value);
              e.currentTarget.value = "";
            }}
            onChange={(e) => {
              // datalist auto-fill fires a "change" event when the
              // operator clicks an option (no Enter); detect that path
              // by comparing against the visible options.
              const picked = e.currentTarget.value;
              const matchedFromList =
                picked.length > 0 &&
                document
                  .getElementById(datalistId)
                  ?.querySelector(`option[value="${CSS.escape(picked)}"]`);
              if (matchedFromList) {
                insertMetric(picked);
                e.currentTarget.value = "";
              }
            }}
          />
        </div>
      )}
      <textarea
        ref={textareaRef}
        className="mono"
        value={value}
        rows={2}
        placeholder='avg by (pool_id) (rate(my_metric[1m]))'
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{ width: "100%", fontSize: 12 }}
        spellCheck={false}
        autoComplete="off"
      />
    </div>
  );
}
